import { z } from 'zod';
import { decimalNoNegativo, decimalPositivo, paginacion, uuid } from './comun.ts';

export const ESTADOS_TRASLADO = ['en_transito', 'recibido_parcial', 'recibido', 'cancelado'] as const;
export type EstadoTraslado = (typeof ESTADOS_TRASLADO)[number];
/** Por qué llegó menos de lo enviado: se quedó en el origen (vuelve a su existencia) o se rompió/perdió en el camino. */
export const CAUSAS_DIFERENCIA = ['origen', 'merma'] as const;
export type CausaDiferencia = (typeof CAUSAS_DIFERENCIA)[number];
/** Resumen de la guía: una sola causa, o «mixta» si unos productos se quedaron en el origen y otros fueron merma. */
export type CausaDiferenciaTraslado = CausaDiferencia | 'mixta';
const texto = (max: number) => z.string().trim().max(max).nullable().optional();

export const trasladoLineaInput = z.object({ producto_id: uuid, cantidad: decimalPositivo });
export const trasladoInput = z.object({
  almacen_origen_id: uuid,
  almacen_destino_id: uuid,
  motivo: z.string().trim().max(300).optional(),
  observaciones: z.string().trim().max(500).optional(),
  /** Transporte (opcional: si no se carga, la guía deja las líneas para llenarlas a mano). */
  equipo_id: uuid.nullable().optional(),
  /** Vehículo que no está en la ficha de flota (ajeno o alquilado): descripción y placa. */
  vehiculo_texto: texto(80),
  chofer_nombre: texto(80),
  chofer_cedula: texto(20),
  lineas: z.array(trasladoLineaInput).min(1, 'Agrega al menos un producto').max(200),
});
export type TrasladoInput = z.infer<typeof trasladoInput>;

export const recepcionInput = z.object({
  /** Unidades recibidas por producto (0 permitido para declarar faltante al cerrar). */
  /** `causa`: por qué no llegó completo ese producto (solo cuenta al cerrar con faltante). */
  lineas: z.array(z.object({ producto_id: uuid, cantidad: decimalNoNegativo, causa: z.enum(CAUSAS_DIFERENCIA).optional() })).min(1).max(200),
  /** true = cierra la guía: lo no recibido se declara faltante (ajuste pendiente en el origen). */
  cerrar: z.boolean().default(false),
  observaciones: z.string().trim().max(500).optional(),
  /**
   * Obligatoria al cerrar con faltante: lo que observó quien recibe y la causa de cada producto que no llegó completo
   * (en su línea, o aquí una sola para todos). La revisa la dueña.
   */
  diferencia: z.object({ causa: z.enum(CAUSAS_DIFERENCIA).optional(), observacion: z.string().trim().min(5, 'Explica qué pasó con lo que falta').max(500) }).optional(),
});
export type RecepcionInput = z.infer<typeof recepcionInput>;

export const cancelarTrasladoInput = z.object({ motivo: z.string().trim().min(3, 'Indica el motivo').max(300) });
/** Visto bueno de la dueña a una diferencia que «quedó en el origen». */
export const revisarDiferenciaInput = z.object({ comentario: z.string().trim().max(300).optional() });

export const trasladosFiltros = paginacion.extend({
  direccion: z.enum(['entrantes', 'salientes']).optional(),
  estado: z.enum(ESTADOS_TRASLADO).optional(),
  q: z.string().trim().max(80).optional(),
  /** Reporte: por fechas de despacho, depósito de origen o de destino, tienda (roles globales) y diferencias. */
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
  almacen_origen_id: uuid.optional(),
  almacen_destino_id: uuid.optional(),
  sucursal_id: uuid.optional(),
  con_diferencia: z.enum(['true', 'false']).optional(),
});
export type TrasladosFiltros = z.infer<typeof trasladosFiltros>;

export interface DestinoTrasladoDTO {
  id: string;
  nombre: string;
  tipo: string;
  principal: boolean;
  sucursal_id: string;
  sucursal_codigo: string;
  sucursal_nombre: string;
}

export interface TrasladoLineaDTO {
  id: string;
  producto_id: string;
  producto_codigo: string;
  producto_nombre: string;
  producto_unidad: string;
  enviado: string;
  recibido: string;
  pendiente: string;
  /** Guía cerrada con faltante de este producto: se quedó en el origen o fue merma del traslado. */
  faltante_causa: CausaDiferencia | null;
}

export interface RecepcionDTO {
  id: string;
  usuario_nombre: string;
  recibido_en: string;
  cierra: boolean;
  observaciones: string | null;
  lineas: { producto_codigo: string; cantidad: string }[];
}

export interface TrasladoResumenDTO {
  id: string;
  numero_formateado: string;
  estado: EstadoTraslado;
  /** Relativo a la sucursal del usuario. */
  direccion: 'entrante' | 'saliente' | 'interno';
  sucursal_origen_nombre: string;
  sucursal_destino_nombre: string;
  almacen_origen_nombre: string;
  almacen_destino_nombre: string;
  despachado_por_nombre: string;
  despachado_en: string;
  lineas: number;
  unidades_enviadas: string;
  unidades_recibidas: string;
  con_discrepancia: boolean;
  /** Vehículo (de la ficha o escrito a mano) y chofer, si se cargaron. */
  vehiculo: string | null;
  chofer_nombre: string | null;
  diferencia_causa: CausaDiferenciaTraslado | null;
  /** La dueña ya revisó la diferencia (visto bueno, o resolvió el ajuste de la merma). */
  diferencia_revisada: boolean;
}

export interface TrasladoDTO extends Omit<TrasladoResumenDTO, 'lineas'> {
  sucursal_origen_id: string;
  sucursal_destino_id: string;
  almacen_origen_id: string;
  almacen_destino_id: string;
  motivo: string | null;
  observaciones: string | null;
  recibido_por_nombre: string | null;
  recibido_en: string | null;
  cerrado_en: string | null;
  ajuste_discrepancia_id: string | null;
  ajuste_discrepancia_numero: string | null;
  cancelado_por_nombre: string | null;
  cancelado_en: string | null;
  motivo_cancelacion: string | null;
  impresiones: number;
  /** Direcciones para la guía: la del depósito o, si no tiene propia, la fiscal de su tienda. */
  origen_direccion: string;
  destino_direccion: string;
  equipo_id: string | null;
  vehiculo_placa: string | null;
  chofer_cedula: string | null;
  diferencia_observacion: string | null;
  diferencia_revisada_por_nombre: string | null;
  diferencia_revisada_en: string | null;
  diferencia_comentario: string | null;
  /** Lo que el usuario actual puede hacer con esta guía. */
  puede_recibir: boolean;
  puede_cancelar: boolean;
  puede_revisar_diferencia: boolean;
  lineas: TrasladoLineaDTO[];
  recepciones: RecepcionDTO[];
}

export interface NotificacionDTO {
  id: string;
  tipo: string;
  titulo: string;
  cuerpo: string | null;
  referencia_tipo: string | null;
  referencia_id: string | null;
  ruta: string | null;
  creado_en: string;
  leida_en: string | null;
}
